import {
  PosVendaFonteFidelidade,
  PosVendaProtocoloLinhaDto,
  PosVendaProtocoloStatus,
  PosVendaResultadoLigacaoWhatsApp,
} from '../../types/posVenda';

const STATUS_COM_VALIDACAO_SUSPENSAO: PosVendaProtocoloStatus[] = ['EmExecucao', 'Concluido'];

/** Alerta visível quando não há fidelidade (#505 3aa9e912). Suspensão permitida. */
export const MENSAGEM_ALERTA_SEM_DADO_FIDELIDADE =
  'Não encontramos informações de fidelidade deste cliente';

/** @deprecated use MENSAGEM_ALERTA_SEM_DADO_FIDELIDADE */
export const MENSAGEM_SEM_DADO_FIDELIDADE = MENSAGEM_ALERTA_SEM_DADO_FIDELIDADE;

export function statusExigeValidacaoSuspensao(status: PosVendaProtocoloStatus): boolean {
  return STATUS_COM_VALIDACAO_SUSPENSAO.includes(status);
}

/**
 * Suspensão permitida quando restam **até** `maxMesesRestantes` (inclusive).
 * Valores negativos = fidelidade vencida (sempre dentro do limite).
 */
export function fidelidadeRestantePermiteSuspensao(
  mesesRestantes: number | null | undefined,
  maxMesesRestantesParaSuspender: number
): boolean {
  if (mesesRestantes == null || Number.isNaN(mesesRestantes)) return false;
  return mesesRestantes <= maxMesesRestantesParaSuspender;
}

/** @deprecated use fidelidadeRestantePermiteSuspensao */
export const fidelidadeRestanteAtendeMinimo = fidelidadeRestantePermiteSuspensao;

export function mesesFidelidadeLinha(linha: PosVendaProtocoloLinhaDto): number | null {
  if (linha.mesesFidelidadeRestantes != null) return linha.mesesFidelidadeRestantes;
  if (linha.coletaSnapshot?.mesesRestantes != null) return linha.coletaSnapshot.mesesRestantes;
  return null;
}

export function fonteFidelidadeLinha(
  linha: PosVendaProtocoloLinhaDto
): PosVendaFonteFidelidade | string | null {
  if (linha.fonteFidelidade != null) return linha.fonteFidelidade;
  const snap = linha.coletaSnapshot?.fonte;
  return snap != null ? snap : null;
}

export function linhaSemDadoFidelidade(linha: PosVendaProtocoloLinhaDto): boolean {
  if (linha.semDadoFidelidade === true) return true;
  if (linha.semDadoFidelidade === false) return false;
  const meses = mesesFidelidadeLinha(linha);
  const fonte = fonteFidelidadeLinha(linha);
  return meses == null && (fonte == null || fonte === 'Indisponivel');
}

/** @deprecated sem dado não bloqueia mais — use linhaSemDadoFidelidade / contractSuspensaoSemDadoFidelidade */
export function semDadoFidelidadeParaBloqueio(
  mesesRestantes: number | null | undefined,
  fonteFidelidade: PosVendaFonteFidelidade | null | undefined
): boolean {
  return (
    mesesRestantes == null && (fonteFidelidade == null || fonteFidelidade === 'Indisponivel')
  );
}

export function formatMesesFidelidadeRestantes(meses: number): string {
  if (meses < 0) {
    const abs = Math.abs(meses);
    return `Fidelidade vencida há ${abs} ${abs === 1 ? 'mês' : 'meses'}`;
  }
  if (meses === 0) return 'Fidelidade encerra neste mês';
  return `Restam ${meses} ${meses === 1 ? 'mês' : 'meses'} de fidelidade`;
}

export function linhaBloqueiaPorFidelidade(
  linha: PosVendaProtocoloLinhaDto,
  maxMesesRestantesParaSuspender: number,
  regraSuspensaoBloqueioAtivo: boolean
): boolean {
  if (!regraSuspensaoBloqueioAtivo) return false;
  if (linhaSemDadoFidelidade(linha)) return false;
  const meses = mesesFidelidadeLinha(linha);
  if (meses == null) return false;
  return !fidelidadeRestantePermiteSuspensao(meses, maxMesesRestantesParaSuspender);
}

export function mensagemFidelidadeLinha(
  linha: PosVendaProtocoloLinhaDto,
  maxMesesRestantesParaSuspender: number
): string {
  if (linhaSemDadoFidelidade(linha)) {
    return MENSAGEM_ALERTA_SEM_DADO_FIDELIDADE;
  }
  const meses = mesesFidelidadeLinha(linha);
  if (meses == null) {
    return MENSAGEM_ALERTA_SEM_DADO_FIDELIDADE;
  }
  const fonte = fonteFidelidadeLinha(linha);
  const rest = formatMesesFidelidadeRestantes(meses);
  const fonteTxt = fonte ? ` (fonte: ${labelFonteFidelidade(fonte)})` : '';
  const dentroDoLimite = fidelidadeRestantePermiteSuspensao(meses, maxMesesRestantesParaSuspender);
  if (dentroDoLimite) {
    return `${rest}${fonteTxt} — dentro do limite (suspensão só com até ${maxMesesRestantesParaSuspender} meses restantes).`;
  }
  return `${rest}${fonteTxt} — suspensão só com até ${maxMesesRestantesParaSuspender} meses restantes.`;
}

export function mensagemFidelidadeRestante(
  mesesRestantes: number | null | undefined,
  maxMesesRestantesParaSuspender: number,
  fonteFidelidade?: PosVendaFonteFidelidade | null
): string {
  const linhaLike: PosVendaProtocoloLinhaDto = {
    contractLineId: '_',
    marcada: true,
    mesesFidelidadeRestantes: mesesRestantes,
    fonteFidelidade: fonteFidelidade ?? undefined,
    semDadoFidelidade:
      mesesRestantes == null &&
      (fonteFidelidade == null || fonteFidelidade === 'Indisponivel')
        ? true
        : undefined,
  };
  return mensagemFidelidadeLinha(linhaLike, maxMesesRestantesParaSuspender);
}

function linhasParaValidacao(linhas: PosVendaProtocoloLinhaDto[]): PosVendaProtocoloLinhaDto[] {
  const marcadas = linhas.filter((l) => l.marcada);
  return marcadas.length > 0 ? marcadas : linhas;
}

export function whatsAppChamouBloqueia(linhas: PosVendaProtocoloLinhaDto[]): string | null {
  for (const linha of linhasParaValidacao(linhas)) {
    if (linha.linhaTemWhatsApp === true && linha.resultadoLigacaoWhatsApp === 'Chamou') {
      const rotulo = linha.numeroLinha ?? linha.contractLineId;
      return `Suspensão bloqueada: na linha ${rotulo} o cliente atendeu a ligação (WhatsApp). Não prossiga com a suspensão neste fluxo.`;
    }
  }
  return null;
}

export function whatsAppPendenteValidacao(linhas: PosVendaProtocoloLinhaDto[]): string | null {
  for (const linha of linhasParaValidacao(linhas)) {
    if (linha.linhaTemWhatsApp !== true) continue;
    const r = linha.resultadoLigacaoWhatsApp;
    if (!r || r === 'NaoVerificado') {
      const rotulo = linha.numeroLinha ?? linha.contractLineId;
      return `Registre a verificação WhatsApp da linha ${rotulo} (Não chamou ou Chamou).`;
    }
  }
  return null;
}

export interface AvaliarBloqueioSuspensaoInput {
  regraSuspensaoBloqueioAtivo: boolean;
  /** Legado — bloqueio de fidelidade é por linha. */
  mesesFidelidadeRestantes?: number | null;
  fonteFidelidade?: PosVendaFonteFidelidade | null;
  contractSuspensaoSemDadoFidelidade?: boolean | null;
  maxMesesRestantesParaSuspender: number;
  linhas: PosVendaProtocoloLinhaDto[];
  statusAlvo: PosVendaProtocoloStatus;
}

export function avaliarBloqueioSuspensao(input: AvaliarBloqueioSuspensaoInput): string | null {
  if (!statusExigeValidacaoSuspensao(input.statusAlvo)) return null;
  if (!input.regraSuspensaoBloqueioAtivo) return null;

  const alvo = linhasParaValidacao(input.linhas);

  for (const linha of alvo) {
    if (linhaBloqueiaPorFidelidade(linha, input.maxMesesRestantesParaSuspender, true)) {
      const rotulo = linha.numeroLinha ?? linha.contractLineId;
      return `${mensagemFidelidadeLinha(linha, input.maxMesesRestantesParaSuspender)} Suspensão bloqueada na linha ${rotulo}: restam mais meses do que o limite permitido.`;
    }
  }

  const pendenteWa = whatsAppPendenteValidacao(input.linhas);
  if (pendenteWa) return pendenteWa;

  return whatsAppChamouBloqueia(input.linhas);
}

export const RESULTADO_LIGACAO_WHATSAPP_LABEL: Record<PosVendaResultadoLigacaoWhatsApp, string> = {
  NaoVerificado: 'Não verificado',
  NaoChamou: 'Não chamou',
  Chamou: 'Chamou',
};

export const FONTE_FIDELIDADE_LABEL: Record<PosVendaFonteFidelidade, string> = {
  Verosfa: 'Verosfa',
  ColetaP2B: 'Coleta P2B',
  ClienteWhatsApp: 'Cliente WhatsApp',
  FidelidadeRestante: 'Fidelidade restante (cliente)',
  'ClienteWhatsApp.FidelidadeRestante': 'Cliente WhatsApp',
  Indisponivel: 'Indisponível',
};

export function labelFonteFidelidade(
  fonte: PosVendaFonteFidelidade | string | null | undefined
): string {
  if (!fonte) return '';
  if (Object.prototype.hasOwnProperty.call(FONTE_FIDELIDADE_LABEL, fonte)) {
    return FONTE_FIDELIDADE_LABEL[fonte as PosVendaFonteFidelidade];
  }
  return fonte;
}

export function semaforoLabelColor(semaforo: string | null | undefined): 'green' | 'yellow' | 'red' | 'grey' {
  if (!semaforo) return 'grey';
  const s = semaforo.toLowerCase();
  if (s.includes('verde') || s === 'green') return 'green';
  if (s.includes('amarelo') || s.includes('yellow')) return 'yellow';
  if (s.includes('vermelho') || s.includes('red')) return 'red';
  return 'grey';
}
